import { Col, Row } from 'react-bootstrap';
import { RtVueApiRoutes } from 'RtExports/routes';
import {
	DataFilter,
	DataFilterOperator,
	DataSources,
	GuardianReconciliationMappingFiltersIndexResponse,
	GuardianReconciliationNoteTypes,
	GuardianReconciliationUsageDetailsProfileResponse,
	GuardianReconciliationUsageDetailsSummaryIndexResponse,
	ReconciliationMappingDataSourceField
} from 'RtModels';
import { CdrSearchViewRouter } from 'RtUi/app/rtVue/CdrSearchView/CdrSearchView.router';
import { RtVueReportUrlSearchParamsCreate } from 'RtUi/app/rtVue/common/lib/containers/lib/RtVueReportUrlSearchParams';
import { ReconciliationNotes } from 'RtUi/app/rtVue/ReconciliationNote/lib/components/ReconciliationNotes';
import { EasyButtonContainer } from 'RtUi/app/rtVue/ReconciliationUsage/lib/components/EasyButtonContainer';
import { ReconciliationUsageBreakdown } from 'RtUi/app/rtVue/ReconciliationUsage/lib/components/ReconciliationUsageBreakdown';
import { ReconciliationUsageDetails } from 'RtUi/app/rtVue/ReconciliationUsage/lib/components/ReconciliationUsageDetails';
import {
	DisputeControlsMode,
	ReconciliationUsageEditor
} from 'RtUi/app/rtVue/ReconciliationUsage/lib/forms/ReconciliationUsageEditor';
import { ReconciliationUsageDetailsSummaryGrid } from 'RtUi/app/rtVue/ReconciliationUsage/lib/grids/ReconciliationUsageDetailsSummaryGrid';
import { IReconciliationDirection } from 'RtUi/app/rtVue/ReconciliationUsage/lib/grids/ReconciliationUsageDialCodeSummaryGrid';
import { ProfileApplicationContainer } from 'RtUi/components/containers/TabbedApplicationContainer';
import { Loading } from 'RtUi/components/ui/Loading';
import { TabbedLayout, TabbedLayoutTab } from 'RtUi/components/ui/TabbedLayout';
import { HttpRequest } from 'RtUi/utils/http/HttpRequest';
import { ReconciliationUsageResource } from './lib/resources/ReconciliationUsageDetailsResource';
import { ReconciliationUsageRouter } from './ReconciliationUsage.router';

interface IReconciliationUsageProfileContainerProps {}

interface IReconciliationUsageProfileContainerState {
	activeTab: string;
	profile?: GuardianReconciliationUsageDetailsProfileResponse;
}

@ReconciliationUsageRouter.getProfileRtUiController()
export class ReconciliationUsageProfileContainer extends ProfileApplicationContainer<
	IReconciliationUsageProfileContainerProps,
	IReconciliationUsageProfileContainerState
> {
	public Tabs = ReconciliationUsageRouter.getProfileTabs();

	public state: IReconciliationUsageProfileContainerState = {
		activeTab: this.Tabs.UsageDetails.header
	};

	public async componentDidMount() {
		const profileId = this.getIdParam();
		const usageResource = new ReconciliationUsageResource();
		const profile = await usageResource.get(profileId);

		this.setState({ profile });
	}

	public render() {
		const { profile } = this.state;

		if (!profile) {
			return <Loading />;
		}

		return (
			<TabbedLayout<GuardianReconciliationUsageDetailsProfileResponse>
				router={ReconciliationUsageRouter}
				profile={profile}
				activeTab={this.state.activeTab}
				onTabChange={(activeTab) => this.setState({ activeTab })}
			>
				<TabbedLayoutTab {...this.Tabs.UsageDetails}>
					<Row>
						<Col xl={9}>
							<ReconciliationUsageEditor
								editMode={profile}
								disputeControlsMode={DisputeControlsMode.Hide}
								onUpdate={(newProfile) =>
									this.setState({ profile: newProfile })
								}
							/>
						</Col>
						<Col xl={3}>
							<ReconciliationNotes
								fullHeight
								reconciliationId={profile.reconciliationId}
								reconciliationNoteType={GuardianReconciliationNoteTypes.Usage}
								reconciliationDetailId={
									profile.guardianReconciliationUsageDetailId
								}
							/>
						</Col>
					</Row>
					<ReconciliationUsageBreakdown className="my-5" profile={profile} />
					<ReconciliationUsageDetailsSummaryGrid
						className="mt-5"
						onDrillDown={this.onDrillDown}
						resourceParams={{
							reconciliationId: profile.reconciliationId
						}}
					/>
				</TabbedLayoutTab>
				<TabbedLayoutTab {...this.Tabs.Details}>
					<ReconciliationUsageDetails profile={profile} />
				</TabbedLayoutTab>
				<TabbedLayoutTab {...this.Tabs.Dispute}>
					<EasyButtonContainer className="mb-5" profile={profile} />
					<ReconciliationUsageEditor
						editMode={profile}
						disputeControlsMode={DisputeControlsMode.HideEverythingElse}
						onUpdate={(newProfile) => this.setState({ profile: newProfile })}
					/>
				</TabbedLayoutTab>
			</TabbedLayout>
		);
	}

	protected onDrillDown = async (
		row: GuardianReconciliationUsageDetailsSummaryIndexResponse,
		direction: IReconciliationDirection
	) => {
		const srcFileStreamIds = this.state.profile!.srcFilestreamIds!;
		const tgtFileStreamIds = this.state.profile!.tgtFilestreamIds!;
		const reconciliationId = this.state.profile?.reconciliationId;

		const mappings = await HttpRequest.fetchWithRoute<
			GuardianReconciliationMappingFiltersIndexResponse[]
		>(RtVueApiRoutes.GuardianReconciliationMappingFilters.Index, {
			params: {
				guardianReconciliationMappingId: this.state.profile?.mappingId,
				dataSourceDirection: direction === 'src' ? 1 : 2
			}
		});

		const allFilters: Record<number, DataFilter> = {};

		for (const mapping of mappings) {
			switch (mapping.dataSourceField) {
				case ReconciliationMappingDataSourceField.TrunkGroup:
					if (
						!(ReconciliationMappingDataSourceField.TrunkGroup in allFilters)
					) {
						allFilters[ReconciliationMappingDataSourceField.TrunkGroup] = {
							key: 'trunkGroup',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.TrunkGroup
					].operands.push({
						dataSource: DataSources.Text,
						value: mapping.dataSourceValue
					});
					break;
				case ReconciliationMappingDataSourceField.Connection:
					if (
						!(ReconciliationMappingDataSourceField.Connection in allFilters)
					) {
						allFilters[ReconciliationMappingDataSourceField.Connection] = {
							key: 'connection',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.Connection
					].operands.push({
						dataSource: DataSources.Text,
						value: mapping.dataSourceValue
					});
					break;
				case ReconciliationMappingDataSourceField.Subscription:
					if (
						!(ReconciliationMappingDataSourceField.Subscription in allFilters)
					) {
						allFilters[ReconciliationMappingDataSourceField.Subscription] = {
							key: 'cost_subscription_id',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.Subscription
					].operands.push({
						dataSource: DataSources.Subscriptions,
						value: mapping.dataSourceValue
					});
					break;
				case ReconciliationMappingDataSourceField.Account:
					if (!(ReconciliationMappingDataSourceField.Account in allFilters)) {
						allFilters[ReconciliationMappingDataSourceField.Account] = {
							key: 'account',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.Account
					].operands.push({
						dataSource: DataSources.Text,
						value: mapping.dataSourceValue
					});
					break;
				case ReconciliationMappingDataSourceField.Switch:
					if (!(ReconciliationMappingDataSourceField.Switch in allFilters)) {
						allFilters[ReconciliationMappingDataSourceField.Switch] = {
							key: 'switch',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[ReconciliationMappingDataSourceField.Switch].operands.push(
						{
							dataSource: DataSources.Text,
							value: mapping.dataSourceValue
						}
					);
					break;
				case ReconciliationMappingDataSourceField.CallType:
					if (!(ReconciliationMappingDataSourceField.CallType in allFilters)) {
						allFilters[ReconciliationMappingDataSourceField.CallType] = {
							key: 'callType',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.CallType
					].operands.push({
						dataSource: DataSources.Text,
						value: mapping.dataSourceValue
					});
					break;
				case ReconciliationMappingDataSourceField.ExternalAccount:
					if (
						!(
							ReconciliationMappingDataSourceField.ExternalAccount in allFilters
						)
					) {
						allFilters[ReconciliationMappingDataSourceField.ExternalAccount] = {
							key: 'externalAccount',
							operands: [],
							operator: DataFilterOperator.In
						};
					}
					allFilters[
						ReconciliationMappingDataSourceField.ExternalAccount
					].operands.push({
						dataSource: DataSources.Text,
						value: mapping.dataSourceValue
					});
					break;
				default:
					break;
			}
		}

		const filters: DataFilter[] = [];
		for (const value of Object.values(allFilters)) {
			if (value.operands.length > 0) {
				filters.push(value);
			}
		}

		const searchParams: RtVueReportUrlSearchParamsCreate = {
			date: row.callDate as unknown as string
		};

		if (typeof reconciliationId === 'number') {
			const reconciliationIdStr = String(reconciliationId);
			if (direction === 'src') {
				searchParams.srcReconciliation = reconciliationIdStr;
				searchParams.fileStreamId = srcFileStreamIds.split(',').join('+');
			}

			if (direction === 'tgt') {
				searchParams.tgtReconciliation = reconciliationIdStr;
				searchParams.fileStreamId = tgtFileStreamIds.split(',').join('+');
			}
		}

		window.open(
			CdrSearchViewRouter.getDrillDownPath(searchParams, filters),
			'_blank'
		);
	};
}
